import { useSnackbar } from "components/haven-design/snackbar";
import { useInterestsTable } from "./interests-table/table-context";
import { useSidebarFilters } from "./sidebar-filters";
import { getBulkLimitError, MAX_BULK_ACTION_APPLICANTS } from "./interests-toolbar/utils";

export type OwnerBulkAction = "assign" | "remove";

type TableSelection = ReturnType<typeof useInterestsTable>;
type OwnerBulkSelection = Pick<TableSelection, "totalSelectedInterests">;
type BulkRowSelection = Pick<TableSelection, "selectedInterestIds" | "totalSelectedInterests">;

const OWNER_BULK_MESSAGES: Record<OwnerBulkAction, { empty: string; overLimit: string }> = {
  assign: {
    empty: "Please select interests to assign an owner.",
    overLimit: `Owners can be assigned to at most ${MAX_BULK_ACTION_APPLICANTS} applicants at once`,
  },
  remove: {
    empty: "Please select interests to remove an owner.",
    overLimit:
      `Owners can be removed from at most ${MAX_BULK_ACTION_APPLICANTS} applicants at once`,
  },
};

// Select-all and page ranges are allowed as long as the selection fits the cap.
export const getOwnerBulkValidationError = (
  action: OwnerBulkAction,
  { totalSelectedInterests }: OwnerBulkSelection,
): string | null => getBulkLimitError(totalSelectedInterests, OWNER_BULK_MESSAGES[action]);

// Bulk scope is list-bound like the Bulk Actions menu, so multi-list views stay single-row.
export const isBulkOwnerRow = (
  id: string,
  { selectedInterestIds, totalSelectedInterests }: BulkRowSelection,
  interestListCount: number,
): boolean =>
  selectedInterestIds.includes(id) && totalSelectedInterests >= 2 && interestListCount === 1;

// `enabled` is false when the host of the bulk modal (the slideout toolbar) isn't mounted.
export const useRequestBulkAssignOwner = (enabled: boolean) => {
  const { openSnackbar } = useSnackbar();
  const table = useInterestsTable();
  const { interestListIds } = useSidebarFilters();

  const isBulkRow = (id: string) => enabled && isBulkOwnerRow(id, table, interestListIds.length);

  const requestBulkAssign = () => {
    const error = getOwnerBulkValidationError("assign", table);
    if (error) openSnackbar({ message: error, status: "error", duration: 5000 });
    else table.setAssignOwnerModalOpen(true);
  };

  return { isBulkRow, requestBulkAssign };
};
